import type { AdminStatsDto } from '@/core/data/admin/admin.interface';
import type { AdminDetailClubDto } from '@/core/data/club/club.interface';
import type { AdminDetailLocationDto } from '@/core/data/location/location.interface';
import { ReportType } from '@/core/data/report/report.type';
import { gambits } from '@/core/gambits';
import type { Result } from '@/core/network/http/httpClient.interface';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamStoreId } from '@/views/composables/constants/main/store.constants';
import type { GamAdminTableColumn } from '@/views/composables/models/components/GamAdminTable';
import type { SimpleTableColumn, SimpleTableRow } from '@/views/composables/models/components/SimpleTable';
import type { ClubForm, LocationFormProps } from '@/views/composables/models/form.interface';
import { defineStore } from 'pinia';

type State = {
  locationColumns: SimpleTableColumn[];
  editingLocation: AdminDetailLocationDto | undefined;
  stats?: AdminStatsDto;
  userColumns: GamAdminTableColumn[];
  userSortBy: string;
  usernameIssueColumns: GamAdminTableColumn[];
  reportColumns: SimpleTableColumn[];
  selectedReportType: ReportType;
  clubColumns: SimpleTableColumn[];
  editingClub: AdminDetailClubDto | undefined;
};

export const useAdminStore = defineStore(GamStoreId.ADMIN, {
  state: (): State => ({
    locationColumns: [
      { id: 'id', label: 'ID' },
      { id: 'name', label: 'Name' },
      { id: 'user', label: 'User' },
      { id: 'createdAt', label: 'Added' },
      { id: 'updatedAt', label: 'Modified' },
    ],
    userColumns: [
      { id: 'email', label: 'Email', width: 'minmax(180px, 2fr)' },
      { id: 'username', label: 'Username', width: 'minmax(120px, 1.3fr)' },
      { id: 'name', label: 'Name', width: 'minmax(120px, 1.5fr)' },
      { id: 'age', label: 'Age', width: '56px', align: 'end' },
      { id: 'loginProvider', label: 'Sign-up', width: '96px' },
      { id: 'createdAt', label: 'Joined', width: '120px', sortKey: 'createdAt' },
      { id: 'lastActiveAt', label: 'Last active', width: '128px', sortKey: 'lastActiveAt' },
      { id: 'actions', label: 'Master · Delete', width: '128px', align: 'end' },
    ],
    userSortBy: '-createdAt',
    usernameIssueColumns: [
      { id: 'username', label: 'Username', width: 'minmax(140px, 1.5fr)' },
      { id: 'email', label: 'Email', width: 'minmax(180px, 2fr)' },
      { id: 'issue', label: 'Issue', width: '128px' },
      { id: 'createdAt', label: 'Joined', width: '120px' },
    ],
    reportColumns: [
      { id: 'id', label: 'ID' },
      { id: 'reason', label: 'Reason' },
      { id: 'description', label: 'Description' },
      { id: 'user', label: 'Reported by' },
      { id: 'createdAt', label: 'Reported' },
    ],
    clubColumns: [
      { id: 'username', label: 'Username' },
      { id: 'name', label: 'Name' },
      { id: 'clubType', label: 'Type' },
      { id: 'updatedAt', label: 'Updated' },
      { id: 'actions', label: 'Actions' },
    ],
    editingLocation: undefined,
    stats: undefined,
    selectedReportType: ReportType.LOCATION,
    editingClub: undefined as AdminDetailClubDto | undefined,
  }),
  getters: {
    reportColumnsByType: (state) => {
      switch (state.selectedReportType) {
        case ReportType.USERS:
          return [
            { id: 'id', label: 'ID' },
            { id: 'reason', label: 'Reason' },
            { id: 'description', label: 'Description' },
            { id: 'reportedUser', label: 'Reported User' },
            { id: 'user', label: 'Reporter' },
            { id: 'createdAt', label: 'Reported' },
          ];
        case ReportType.GAMBIT:
          return [
            { id: 'id', label: 'ID' },
            { id: 'reason', label: 'Reason' },
            { id: 'description', label: 'Description' },
            { id: 'reportedGambit', label: 'Reported Gambit' },
            { id: 'user', label: 'Reporter' },
            { id: 'createdAt', label: 'Reported' },
          ];
        case ReportType.CLUB:
          return [
            { id: 'id', label: 'ID' },
            { id: 'reason', label: 'Reason' },
            { id: 'description', label: 'Description' },
            { id: 'reportedClub', label: 'Reported Club' },
            { id: 'user', label: 'Reporter' },
            { id: 'createdAt', label: 'Reported' },
          ];
        case ReportType.BLOB:
          return [
            { id: 'id', label: 'ID' },
            { id: 'reason', label: 'Reason' },
            { id: 'description', label: 'Description' },
            { id: 'reportedBlob', label: 'Reported Image' },
            { id: 'user', label: 'Reporter' },
            { id: 'createdAt', label: 'Reported' },
          ];
        case ReportType.LOCATION:
          return [
            { id: 'id', label: 'ID' },
            { id: 'reason', label: 'Reason' },
            { id: 'description', label: 'Description' },
            { id: 'locationId', label: 'Reported Location' },
            { id: 'user', label: 'Reporter' },
            { id: 'createdAt', label: 'Reported' },
          ];
        default:
          return state.reportColumns;
      }
    },
  },
  actions: {
    fetchLocations: async (listId: GamListId): Promise<Result<SimpleTableRow[]>> => {
      const result = await gambits.adminService.getLocations(listId);
      return {
        ...result,
        data: result.data,
      };
    },
    async editLocation(id: string): Promise<void> {
      const locationResult = await gambits.adminService.getLocationById(id);
      this.editingLocation = locationResult.data || undefined;
    },
    async setLocationVerified(id: string, verified: boolean): Promise<Result<boolean>> {
      if (this.editingLocation) return { data: false };

      return await gambits.adminService.verifyLocation(id, verified);
    },
    async setLocationVetted(id: string, isVetted: boolean): Promise<Result<boolean>> {
      if (this.editingLocation) return { data: false };

      return await gambits.adminService.vetLocation(id, isVetted);
    },
    async setLocationSparked(id: string, isSparked: boolean): Promise<Result<boolean>> {
      if (this.editingLocation) return { data: false };

      return await gambits.adminService.sparkLocation(id, isSparked);
    },
    async updateLocation(updatedLocation: LocationFormProps): Promise<void> {
      if (!this.editingLocation) return;

      await gambits.adminService.updateLocation({
        ...this.editingLocation,
        ...updatedLocation,
      });
    },
    async moveUser(userId: string, oldGambit: string, newGambit: string): Promise<void> {
      await gambits.adminService.moveUser(userId, oldGambit, newGambit);
    },
    async deleteLocation(id?: string): Promise<void> {
      if (!id) return;

      await gambits.adminService.deleteLocation(id);
      await this.fetchLocations(GamListId.ADMIN_LOCATIONS);
    },
    async fetchStats(): Promise<void> {
      const statsResult = await gambits.adminService.fetchStats();
      this.stats = statsResult.data;
    },
    async fetchSparks(listId: GamListId): Promise<Result<SimpleTableRow[]>> {
      return await gambits.adminService.getSparks(listId);
    },
    async patchSpark(id: string, payload: any): Promise<void> {
      if (!id) return;

      await gambits.adminService.patchSpark(id, payload);
      await this.fetchSparks(GamListId.ADMIN_SPARKS);
    },
    async deletSpark(id?: string): Promise<void> {
      if (!id) return;

      await gambits.adminService.deleteSpark(id);
      await this.fetchSparks(GamListId.ADMIN_SPARKS);
    },
    async fetchUsers(listId: GamListId): Promise<Result<SimpleTableRow[]>> {
      return await gambits.adminService.getUsers(listId, this.userSortBy);
    },
    async fetchUsernameIssues(listId: GamListId): Promise<Result<SimpleTableRow[]>> {
      return await gambits.adminService.getUsernameIssues(listId);
    },
    setUserSortBy(sortBy: string): void {
      this.userSortBy = sortBy;
    },
    async deleteUser(id?: string): Promise<void> {
      if (!id) return;

      await gambits.adminService.deleteUser(id);
      await this.fetchUsers(GamListId.ADMIN_USERS);
    },
    async setUserGambitsMaster(id: string, isGambitsMaster: boolean): Promise<Result<boolean>> {
      return await gambits.adminService.setUserGambitsMaster(id, isGambitsMaster);
    },
    async fetchReports(listId: GamListId): Promise<Result<SimpleTableRow[]>> {
      return await gambits.adminService.getReports(listId, this.selectedReportType);
    },
    setReportType(type: ReportType): void {
      this.selectedReportType = type;
    },
    async fetchClubs(): Promise<Result<SimpleTableRow[]>> {
      const result = await gambits.clubService.getClubs();
      return {
        data:
          result.data?.map((club: any) => ({
            id: club.id,
            username: club.username,
            name: club.name,
            clubType: club.clubType,
            createdAt: club.createdAt,
            updatedAt: club.updatedAt,
          })) || [],
        cursor: undefined,
      };
    },
    async editClub(id: string): Promise<void> {
      const clubResult = await gambits.clubService.getClub(id);
      this.editingClub = clubResult.data || undefined;
    },
    async updateClub(updatedClub: ClubForm): Promise<void> {
      if (!this.editingClub) return;
      await gambits.clubService.updateClub(this.editingClub.id, updatedClub);
      this.editingClub = undefined;
      await this.fetchClubs();
    },
    async deleteClub(id?: string): Promise<void> {
      if (!id) return;
      await gambits.clubService.deleteClub(id);
      await this.fetchClubs();
    },
  },
});
